import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { MunicipalityWithRegion } from 'services/api';
import type { MunicipalitiesState } from './types';
import {
  getMunicipalitiesListAction,
  createMunicipalityAction,
  updateMunicipalityAction,
  removeMunicipalityAction,
} from './actions';

const initialState: MunicipalitiesState = {
  municipalities: [],
  total: 0,
  selectedMunicipality: null,
  loading: false,
};

const municipalitiesSlice = createSlice({
  name: 'municipalities',
  initialState,
  reducers: {
    setSelectedMunicipality: (state, action: PayloadAction<MunicipalityWithRegion | null>) => {
      state.selectedMunicipality = action.payload;
    },
    clearSelectedMunicipality: (state) => {
      state.selectedMunicipality = null;
    },
    clearState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Get municipalities list
    builder.addCase(getMunicipalitiesListAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getMunicipalitiesListAction.fulfilled, (state, action) => {
      state.loading = false;
      state.municipalities = action.payload.content;
      state.total = action.payload.totalElements;
    });
    builder.addCase(getMunicipalitiesListAction.rejected, (state) => {
      state.loading = false;
    });

    // Create municipality
    builder.addCase(createMunicipalityAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createMunicipalityAction.fulfilled, (state, action) => {
      state.loading = false;
      state.municipalities = [action.payload, ...state.municipalities];
      state.total += 1;
    });
    builder.addCase(createMunicipalityAction.rejected, (state) => {
      state.loading = false;
    });

    // Update municipality
    builder.addCase(updateMunicipalityAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateMunicipalityAction.fulfilled, (state, action) => {
      state.loading = false;
      state.municipalities = state.municipalities.map((municipality) =>
        municipality.id === action.payload.id ? action.payload : municipality,
      );
      state.selectedMunicipality = null;
    });
    builder.addCase(updateMunicipalityAction.rejected, (state) => {
      state.loading = false;
    });

    // Remove municipality
    builder.addCase(removeMunicipalityAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(removeMunicipalityAction.fulfilled, (state, action) => {
      state.loading = false;
      state.municipalities = state.municipalities.filter(
        (municipality) => municipality.id !== action.payload,
      );
      state.total -= 1;
      state.selectedMunicipality = null;
    });
    builder.addCase(removeMunicipalityAction.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const municipalitiesReducer = municipalitiesSlice.reducer;
export const { setSelectedMunicipality, clearSelectedMunicipality, clearState } =
  municipalitiesSlice.actions;
